'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Полоса загрузки вверху страницы, как в браузере.
 *
 * Зачем: в App Router переход по ссылке сначала уходит на сервер, и пока
 * ответ не пришёл, страница остаётся прежней. На медленной сети это
 * выглядит как «клик ничего не сделал», человек жмёт второй раз. Полоса
 * говорит: запрос пошёл.
 *
 * Событий роутера в App Router нет, поэтому старт ловим по клику на
 * внутренней ссылке, а финиш — по смене pathname/searchParams.
 *
 * Слушаем в фазе перехвата: Next Link вызывает preventDefault в своём
 * обработчике на самом теге, то есть раньше документа, и в фазе
 * всплытия каждый клик выглядел бы уже отменённым. Взамен сами
 * проверяем всё, из-за чего переход мог бы не состояться: модификаторы,
 * target, download, чужой origin, тот же адрес.
 */
function NavigationProgressBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [percent, setPercent] = useState(0);
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const tick = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (tick.current) {
      clearInterval(tick.current);
      tick.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clearTimers();
    activeRef.current = true;
    setActive(true);
    setPercent(8);
    // Ползём к 90% и там ждём ответа: дорисовать до конца имеет право
    // только реальная смена адреса, иначе полоса врёт.
    tick.current = setInterval(() => {
      setPercent((p) => (p >= 90 ? p : p + Math.max(0.6, (90 - p) / 14)));
    }, 120);
  }, [clearTimers]);

  const done = useCallback(() => {
    clearTimers();
    activeRef.current = false;
    setPercent(100);
    timers.current.push(
      setTimeout(() => {
        setActive(false);
        timers.current.push(
          setTimeout(() => {
            setPercent(0);
          }, 200),
        );
      }, 180),
    );
  }, [clearTimers]);

  // Финиш: адрес сменился, новая страница отрисована.
  useEffect(() => {
    if (activeRef.current) done();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // Тот же адрес: перехода не будет, финиш никогда не наступит.
      if (url.pathname === window.location.pathname && url.search === window.location.search) {
        return;
      }

      start();
    };

    const onPopState = () => {
      start();
    };

    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPopState);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, [start]);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5"
      data-testid="navigation-progress"
      data-active={active ? 'true' : 'false'}
    >
      <div
        className="h-full bg-[var(--color-accent-500)] transition-[width,opacity] duration-200 ease-out"
        style={{
          width: `${String(percent)}%`,
          opacity: percent > 0 ? 1 : 0,
          boxShadow: '0 0 10px var(--color-accent-400), 0 0 4px var(--color-accent-400)',
        }}
      />
    </div>
  );
}

/**
 * useSearchParams требует границы Suspense, иначе страницы уходят
 * в client-side rendering целиком и теряют пререндер.
 */
export function NavigationProgress() {
  return (
    <Suspense fallback={null}>
      <NavigationProgressBar />
    </Suspense>
  );
}
